import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AppShell } from './layouts/AppShell';
import { Landing } from './pages/Landing';
import { Login } from './pages/Login';
import { NotFound } from './pages/NotFound';
import { ThemeProvider } from './lib/theme';
import { AuthProvider, RequireAuth } from './lib/auth';
import { RouteFallback } from './components/RouteFallback';
import './styles/globals.css';

// Eager(위 import): Landing(홈), Login(진입), AppShell(공통 셸), NotFound(폴백).
// 나머지 페이지는 라우트 단위로 코드 분할 — 방문 시점에 해당 청크만 로드한다.
// (named export 라 .then 으로 default 형태로 맞춰줌)
const Features = lazy(() => import('./pages/Features').then((m) => ({ default: m.Features })));
const Pricing = lazy(() => import('./pages/Pricing').then((m) => ({ default: m.Pricing })));
const Help = lazy(() => import('./pages/Help').then((m) => ({ default: m.Help })));
const Company = lazy(() => import('./pages/Company').then((m) => ({ default: m.Company })));
const Privacy = lazy(() => import('./pages/Privacy').then((m) => ({ default: m.Privacy })));
const PublicShare = lazy(() => import('./pages/PublicShare').then((m) => ({ default: m.PublicShare })));
const Dashboard = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const Projects = lazy(() => import('./pages/Projects').then((m) => ({ default: m.Projects })));
const ProjectNew = lazy(() => import('./pages/ProjectNew').then((m) => ({ default: m.ProjectNew })));
const ProjectSchedule = lazy(() => import('./pages/ProjectSchedule').then((m) => ({ default: m.ProjectSchedule })));
const ProjectBudget = lazy(() => import('./pages/ProjectBudget').then((m) => ({ default: m.ProjectBudget })));
const ASRequests = lazy(() => import('./pages/ASRequests').then((m) => ({ default: m.ASRequests })));
const Subcontractors = lazy(() => import('./pages/Subcontractors').then((m) => ({ default: m.Subcontractors })));
const Invoices = lazy(() => import('./pages/Invoices').then((m) => ({ default: m.Invoices })));
const Quotes = lazy(() => import('./pages/Quotes').then((m) => ({ default: m.Quotes })));
const QuotePrint = lazy(() => import('./pages/QuotePrint').then((m) => ({ default: m.QuotePrint })));
const Leads = lazy(() => import('./pages/Leads').then((m) => ({ default: m.Leads })));
const LeadDetail = lazy(() => import('./pages/LeadDetail').then((m) => ({ default: m.LeadDetail })));
const Materials = lazy(() => import('./pages/Materials').then((m) => ({ default: m.Materials })));
const Suppliers = lazy(() => import('./pages/Suppliers').then((m) => ({ default: m.Suppliers })));
const PurchaseOrders = lazy(() => import('./pages/PurchaseOrders').then((m) => ({ default: m.PurchaseOrders })));
const PurchaseOrderForm = lazy(() => import('./pages/PurchaseOrderForm').then((m) => ({ default: m.PurchaseOrderForm })));
const PurchaseOrderDetail = lazy(() => import('./pages/PurchaseOrderDetail').then((m) => ({ default: m.PurchaseOrderDetail })));
const Admin = lazy(() => import('./pages/Admin').then((m) => ({ default: m.Admin })));
const Super = lazy(() => import('./pages/Super').then((m) => ({ default: m.Super })));
const SuperTenantDetail = lazy(() => import('./pages/SuperTenantDetail').then((m) => ({ default: m.SuperTenantDetail })));

// F-5: bare /app/projects/:id 진입 시 예산 화면으로 보낸다 (공사 인덱스 없음).
function ProjectIndexRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/app/projects/${id}/budget`} replace />;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <Suspense fallback={<RouteFallback full />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/features" element={<Features />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/help" element={<Help />} />
              <Route path="/company" element={<Company />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/login" element={<Login />} />
              <Route path="/share/projects/:token" element={<PublicShare />} />
              <Route
                path="/super"
                element={
                  <RequireAuth>
                    <Super />
                  </RequireAuth>
                }
              />
              <Route
                path="/super/tenants/:id"
                element={
                  <RequireAuth>
                    <SuperTenantDetail />
                  </RequireAuth>
                }
              />
              <Route
                path="/app/quotes/:id/print"
                element={
                  <RequireAuth>
                    <QuotePrint />
                  </RequireAuth>
                }
              />
              <Route
                element={
                  <RequireAuth>
                    <AppShell />
                  </RequireAuth>
                }
              >
                <Route path="/app" element={<Dashboard />} />
                <Route path="/app/projects" element={<Projects />} />
                <Route path="/app/projects/new" element={<ProjectNew />} />
                <Route
                  path="/app/projects/:id"
                  element={<ProjectIndexRedirect />}
                />
                <Route
                  path="/app/projects/:id/schedule"
                  element={<ProjectSchedule />}
                />
                <Route
                  path="/app/projects/:id/budget"
                  element={<ProjectBudget />}
                />
                <Route path="/app/leads" element={<Leads />} />
                <Route path="/app/leads/:id" element={<LeadDetail />} />
                <Route path="/app/quotes" element={<Quotes />} />
                <Route path="/app/materials" element={<Materials />} />
                <Route path="/app/suppliers" element={<Suppliers />} />
                <Route path="/app/purchase-orders" element={<PurchaseOrders />} />
                <Route
                  path="/app/purchase-orders/new"
                  element={<PurchaseOrderForm />}
                />
                <Route
                  path="/app/purchase-orders/:id"
                  element={<PurchaseOrderDetail />}
                />
                <Route
                  path="/app/purchase-orders/:id/edit"
                  element={<PurchaseOrderForm />}
                />
                <Route path="/app/subcontractors" element={<Subcontractors />} />
                <Route path="/app/invoices" element={<Invoices />} />
                <Route path="/app/as-requests" element={<ASRequests />} />
                <Route path="/app/admin" element={<Admin />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  </React.StrictMode>,
);
